import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ImportPurchaseRequest } from '@/api/handlers/resources/purchases/requests';
import type { PurchaseInvoiceDetailResponse } from '@/api/handlers/resources/purchases/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { ProductQueryKeys } from '../products/productQueryKeys';
import {
    invalidateDraftInvoiceCaches,
    removeDraftInvoiceRow,
} from './draftInvoiceCache';
import { PurchaseQueryKeys } from './purchaseQueryKeys';

/**
 * Final import (`POST /purchases/import`, bos-api BOS-57) — creates a
 * Received invoice; with `draftId` the named Draft transitions in place. On
 * success every list the /purchases page shows is refreshed: import history
 * (the new row), the drafts list + summary (a transitioned draft falls
 * off), and the draft's row/detail caches are dropped immediately so it
 * never flashes back. The product caches are invalidated too — the import
 * creates products (new-mode lines) and writes stock/batches (restocks), so
 * a cached Products list/detail would otherwise show stale quantities for
 * its staleTime. The query function never rejects — the form branches on
 * `result.success` and surfaces the server's 400 `error` text as-is (do NOT
 * retry on 400).
 */
export const useImportPurchaseMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<PurchaseInvoiceDetailResponse>,
        Error,
        ImportPurchaseRequest
    >({
        mutationFn: (body) => apiClient.purchases.importPurchase(body),
        onSuccess: (result, variables) => {
            if (!result.success) return;
            if (variables.draftId) {
                // The draft became a Received invoice server-side — drop its
                // row/detail now rather than waiting for the refetch.
                removeDraftInvoiceRow(queryClient, variables.draftId);
                queryClient.removeQueries({
                    queryKey: PurchaseQueryKeys.draftInvoice(variables.draftId),
                });
            }
            invalidateDraftInvoiceCaches(queryClient);
            void queryClient.invalidateQueries({
                queryKey: PurchaseQueryKeys.importHistories(),
            });
            void queryClient.invalidateQueries({
                queryKey: ProductQueryKeys.all,
            });
        },
    });
};
